import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function ProtectedRoute({ children, requireApproval = false, adminOnly = false, requireRole = null, requireActiveMembership = false }) {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireApproval && userProfile?.status !== 'approved') {
    return <Navigate to="/pending-approval" />;
  }

  // Lapsed membership gate. Blocks ONLY members whose membership is explicitly
  // cancelled / failed / expired. Admins are exempt, and members with no
  // membershipStatus (grandfathered / comped) or 'active' pass through — so we
  // never lock out legitimate members. NOTE: this is membership status only;
  // paying for an EVENT never sets membershipStatus, so event-only attendees are
  // unaffected (and they aren't members anyway).
  if (requireActiveMembership && userProfile?.role !== 'admin') {
    const ms = userProfile?.membershipStatus;
    if (ms === 'cancelled' || ms === 'payment_failed' || ms === 'expired') {
      return <Navigate to="/membership/renew" replace />;
    }
  }

  // Allow both admin and super_admin (hierarchical) — a strict role === 'admin'
  // check was locking super_admins out of every admin route.
  if (adminOnly && !['admin', 'super_admin'].includes(userProfile?.role)) {
    return <Navigate to="/dashboard" />;
  }

  if (requireRole && userProfile?.role !== requireRole) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
          <p className="text-sm text-gray-500">Required role: {requireRole}</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 bg-apn-blue text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
}
